import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Wrench, RefreshCw, Sparkles, Loader2, AlertCircle, FileText } from 'lucide-react';
import Modal from '../Modal/Modal';
import changelogService from '../../services/changelog.service';
import type { ChangelogResponse } from '../../types/changelog.types';
import './ChangelogModal.css';

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Определяет категорию записи по префиксу и возвращает иконку.
 */
function getItemIcon(text: string): { icon: React.ReactNode; label: string } {
  const lower = text.toLowerCase();
  if (lower.startsWith('добавлено:')) {
    return {
      icon: <Plus size={14} className="changelog-item-icon changelog-item-icon--added" />,
      label: 'добавлено',
    };
  }
  if (lower.startsWith('исправлено:')) {
    return {
      icon: <Wrench size={14} className="changelog-item-icon changelog-item-icon--fixed" />,
      label: 'исправлено',
    };
  }
  if (lower.startsWith('изменено:')) {
    return {
      icon: <RefreshCw size={14} className="changelog-item-icon changelog-item-icon--changed" />,
      label: 'изменено',
    };
  }
  return {
    icon: <FileText size={14} className="changelog-item-icon changelog-item-icon--changed" />,
    label: '',
  };
}

/**
 * Форматирует дату из ISO (2026-02-22) в читаемый вид.
 */
function formatDate(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-');
    const months = [
      'янв', 'фев', 'мар', 'апр', 'май', 'июн',
      'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
    ];
    return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
  } catch {
    return dateStr;
  }
}

const ChangelogModal: React.FC<ChangelogModalProps> = ({ isOpen, onClose }) => {
  const [data, setData] = useState<ChangelogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await changelogService.getChangelog();
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Не удалось загрузить журнал изменений');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !data) {
      fetchData();
    }
  }, [isOpen, data, fetchData]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Что нового?" size="lg">
      {loading && (
        <div className="changelog-loading">
          <Loader2 size={24} className="animate-spin" />
          <span>Загрузка...</span>
        </div>
      )}

      {error && (
        <div className="changelog-error">
          <AlertCircle size={24} />
          <span>{error}</span>
          <button className="changelog-retry-btn" onClick={fetchData}>
            Повторить
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="changelog-content">
          <div className="changelog-current-version">
            <Sparkles size={16} />
            <span>Текущая версия: {data.current_version}</span>
          </div>

          {data.entries.length === 0 ? (
            <div className="changelog-empty">
              <FileText size={24} />
              <span>Нет записей</span>
            </div>
          ) : (
            data.entries.map((entry) => (
              <div className="changelog-entry" key={entry.version}>
                <div className="changelog-entry-header">
                  <span className="changelog-entry-version">v{entry.version}</span>
                  <span className="changelog-entry-date">{formatDate(entry.date)}</span>
                </div>
                <ul className="changelog-items">
                  {entry.items.map((item, idx) => {
                    const { icon } = getItemIcon(item);
                    return (
                      <li className="changelog-item" key={idx}>
                        {icon}
                        <span className="changelog-item-text">{item}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </Modal>
  );
};

export default ChangelogModal;
